<template>
  <ModalDialog
    ref="dialog"
    title="Import DXF"
    :submitting="submitting"
    :submitDisabled="submitting || !importAvailable"
    submitTitle="Import"
    :scrollable="false"
    icon="fa-upload"
    sizeClass="modal-xl"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="dialogForm">
      <div v-if="!hasFiles && !processing && !loading">
        <FormUploadDropTarget title="Drop DXF files here to import" @filesDropped="onFilesDropped">
          <ContentTitle>Import DXF File</ContentTitle>
          <FormRow label="DXF File">
            <template v-slot:label>
              <InlineHelpPopover title="DXF Import" placement="right">
                <p>Shapes are auto detected in DXF files. For best results, use the following guidelines:</p>
                <ul>
                  <li>Ensure the object outline is connected and closed</li>
                  <li>Move additional objects (eg, dimensions, datum lines) into a seperate layer, or with a gap away from the object outline itself</li>
                  <li>Use a different line type (eg, dashed) for reference lines</li>
                  <li>Use circle objects for center cutouts rather than point objects to provide the cutout diameter</li>
                </ul>
              </InlineHelpPopover>
            </template>
            <FormUpload :singleFile="true" :fileTypes="['dxf']" :autoUpload="false" @fileSelected="onFilesDropped" />
            <div class="mt-2">
              <a href="/documents/Synsoft-DXF-Import-Guidelines-v1.0.pdf" target="_blank">DXF Import Guidelines <i class="far fa-external-link"></i></a>
            </div>
          </FormRow>
          <FormRow label="DXF Units">
            <template v-slot:label>
              <InlineHelpPopover title="DXF Units" placement="right">
                <p>Units the DXF file is in.</p>
                <p>If not in millimetres, objects in the DXF file will be scaled to millimetres for the editor.</p>
              </InlineHelpPopover>
            </template>
            <FormSelect v-model="importOptions.unitsType" :options="UnitScaleTypeLookup" />
          </FormRow>
          <FormRow label="Shape Detection Options">
            <FormCheckbox label="Same line types only" v-model="importOptions.sameLineTypeOnly" /><br />
            <FormCheckbox label="Same layer only" v-model="importOptions.sameLayerOnly" /><br />
            <FormCheckbox label="Same colour only" v-model="importOptions.sameColourOnly" /><br />
            <FormCheckbox label="Ignore Overlapped Lines" v-model="importOptions.ignoreOverlappingLines" /><br />
          </FormRow>
        </FormUploadDropTarget>
      </div>


      <LoadingPanel :loading="processing || loading" :loadingError="loadingError" />
      <div class="text-center text-muted mt-4" v-if="processing">Importing data...</div>
      <div class="text-center text-muted mt-4" v-if="loading">Loading...</div>
      
      <div v-if="!processing && !loading && hasFiles && !loadingError && !hasGlassShapes">
        <NotificationBar type="error">
          <NotificationBarTitle>Import Error</NotificationBarTitle>
          No shapes detected in DXF import. Try adjusting shape detection options and try again.<br />
          For best results, use the following guidelines:
          <p class="well m-0">
            <ul>
              <li>Ensure the object outline is connected and closed</li>
              <li>Move additional objects (eg, dimensions, datum lines) into a seperate layer, or with a gap away from the object outline itself</li>
              <li>Use a different line type (eg, dashed) for reference lines</li>
              <li>Use circle objects for center cutouts rather than point objects to provide the cutout diameter</li>
            </ul>
          </p>
          <br />
          <NotificationBarButton @click="resetImport" icon="fa-step-backward">Back</NotificationBarButton>  
        </NotificationBar>
      </div>

      <div v-if="!processing && !loading && hasFiles && !loadingError && hasGlassShapes">
        <FormRow label="Default Stock">
          <FormSearchableSelect ref="stockCodeEntry" v-model="orderItemData.stockId" :options="stockLookupList" placeholder="Stock Code" field="stockId" />
        </FormRow>
        <div v-if="hasStock">
          <Spinner v-if="loadingStock" />
          <div v-if="hasStock && orderItemData.stock">
            <FormRow label="Price Group">
              <FormSelect v-model="orderItemData.stockPriceGroupId" :options="stockPriceGroupLookup" placeholder="Price Group" field="stockPriceGroupId" />
            </FormRow>
            <FormRow label="Stamped" v-if="orderItemData.isStampAvailable">
              <FormCheckbox v-model="orderItemData.isStamped" placeholder="Stamped" field="isStamped" />
            </FormRow>
          </div>
        </div>
        <FormHorizontalRule />
        <ContentTitle>Shape List</ContentTitle>
        <Scroller :maxHeight="350">
          <StaticDataTable class="static-table-list">
            <template v-slot:header>
              <tr>
                <th>Shape</th>
                <th>Height</th>
                <th>Width</th>
                <th>Center Cutouts</th>
                <th>Info</th>
                <th>Import</th>
              </tr>
            </template>
            <template v-slot:body>
              <tr v-for="(shape, index) of glassShapeDataList" :key="index">
                <td>
                  <strong>Shape #{{index + 1}}</strong>
                </td>
                <td>{{shape.height}}mm</td>
                <td>{{shape.width}}mm</td>
                <td>{{shape.holeList.length}}</td>
                <td>
                  <div v-if="shape.warningList.length > 0">
                    <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="text-warning" fixedWidth /> {{shape.warningList.join(', ')}}
                  </div>
                  <div v-if="shape.warningList.length == 0">-</div>
                </td>
                <td>
                  <FormCheckbox v-model="shape.import" />
                </td>
              </tr>
            </template>
            <template v-slot:empty v-if="glassShapeDataList.length == 0">No valid shapes found</template>
          </StaticDataTable>
        </Scroller>
      </div>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import FilterUtils from '@/utils/filterUtils'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import EditorDXFReader from '@glass-designer/utils/editorDXFReader'

import { InventoryStockTypes, PriceTypes, FilterOptionTypes } from '@/store/types'
import { UnitScaleTypes, UnitScaleTypeLookup } from '@glass-designer/types'

export default {
  name: 'ImportDXFDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    FormInputStockCode
  },
  props: {
    contactId: Number,
    defaultSalesRateId: Number,      // Note: Used if contactId is not set    
    editorConfigData: Object
  },
  data() {
    return {
      dialogForm: {},
      hasFiles: false,
      processing: false,
      loading: false,
      loadingError: '',
      loadingStock: false,
      glassShapeDataList: [],
      submitting: false,

      orderItemData: {
        stockId: null,
        stockPriceGroupId: null,
        stock: null,
        description: '',
        quantity: 1,
        thickness: 0,
        unitPrice: 0,
        minChargePrice: 0,
        minChargeQuantity: 0,
        minChargeInvoice: 0,
        isStamped: false,
        isStampAvailable: false,
      },
      importOptions: {
        unitsType: UnitScaleTypes.millimetres,
        sameLineTypeOnly: true,
        sameLayerOnly: true,
        sameColourOnly: true,
        ignoreOverlappingLines: true
      },
      
      defaultPriceGroupId: 0,
      stockList: [],
      stockLookupList: [],
      priceGroupList: [],
      globalDefaultEdgeworkList: [],
      stockDefaultEdgeworkList: [],
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.UnitScaleTypeLookup = UnitScaleTypeLookup
  },
  watch: {
    'orderItemData.stockId': function() {
      this.loadStockData()
    },
    'orderItemData.stockPriceGroupId': function() {
      this.queryStockPrice()
      this.updateStampConfig()
    }
  },
  mounted() {
  },
  computed: {
    importAvailable() {
      let importList = this.glassShapeDataList.filter(shapeData => shapeData.import)

      return importList.length > 0 && !this.processing && !this.loading
    },
    hasStock() {
      return this.orderItemData.stockId != null
    },
    availableStockPriceGroups() {
      if(this.orderItemData.stock && this.orderItemData.stock.glassSalesPriceList) {
        return this.orderItemData.stock.glassSalesPriceList.filter(salesPrice => salesPrice.isActive).map(salesPrice => {
          return {
            id: salesPrice.stockPriceGroupId,
            name: salesPrice.name
          }
        })
      }
      return []
    },
    stockPriceGroupLookup() {
      return this.availableStockPriceGroups
      .filter(priceGroup => {
        let priceGroupInfo = this.priceGroupList.find(pg => pg.id == priceGroup.id)
        if(!priceGroupInfo)
          return false

        // Don't allow stock linked price groups for imports
        return !priceGroupInfo.isStockLinked
      })
      .map(priceGroup => {
        return {
          value: priceGroup.id,
          name: priceGroup.name
        }
      })
    },
    hasGlassShapes() {
      return this.glassShapeDataList.length > 0
    }
  },
  methods: {
    show(item = {}) {
      // Reset form
      this.dialogForm = { }
      
      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.dialogForm = this.clearFormResponse()

      let stockPriceGroup = this.priceGroupList.find(priceGroup => priceGroup.id == this.orderItemData.stockPriceGroupId)
      let isPriceGroupAvailable = this.availableStockPriceGroups.find(priceGroup => priceGroup.id == this.orderItemData.stockPriceGroupId)

      if(!this.orderItemData.stockId)
        this.dialogForm = this.addFormFieldError(this.dialogForm, 'stockId', 'Stock must be set')
      if(!this.orderItemData.stockPriceGroupId || !stockPriceGroup || !isPriceGroupAvailable)
        this.dialogForm = this.addFormFieldError(this.dialogForm, 'stockPriceGroupId', 'Price group must be set')

      if(this.formResponseHasErrors(this.dialogForm))
        return

      // Get the default edgework required for stock/pricegroup combination
      // Stock specific edgework overrides the global setting, even if it turns it off
      var defaultEdgeworkConfigId = 0
      let globalEdgeworkConfig = this.globalDefaultEdgeworkList.find(edgeConfig => edgeConfig.stockPriceGroupId == this.orderItemData.stockPriceGroupId)
      let stockEdgeworkConfig = this.stockDefaultEdgeworkList.find(edgeConfig => edgeConfig.stockPriceGroupId == this.orderItemData.stockPriceGroupId)
      if(globalEdgeworkConfig && globalEdgeworkConfig.orderEditorEdgeworkConfigId)
        defaultEdgeworkConfigId = globalEdgeworkConfig.orderEditorEdgeworkConfigId
      if(stockEdgeworkConfig)
        defaultEdgeworkConfigId = stockEdgeworkConfig.orderEditorEdgeworkConfigId

      var importData = {
        stockId: this.orderItemData.stockId,
        stockPriceGroupId: this.orderItemData.stockPriceGroupId,
        description: this.orderItemData.description,
        stock: this.orderItemData.stock,
        stockPriceGroup: stockPriceGroup,
        unitPrice: this.orderItemData.unitPrice,
        shapeList: this.glassShapeDataList.filter(shapeData => shapeData.import),
        defaultEdgeworkConfigId: defaultEdgeworkConfigId,
        isStamped: this.orderItemData.isStamped
      }

      this.submitting = true
      
      // Hack: Add slight delay after updating submitting state before processing as processing can take some time
      this.$nextTick(() => {
        setTimeout(() => {
          this.modalClosed(importData)
          this.hide()
        }, 500)
      })
    },
    resetImport() {
      this.hasFiles = false
      this.processing = false
      this.loading = false
      this.loadingError = ''
      this.loadingStock = false
      this.glassShapeDataList = []
    },
    onFilesDropped(files) {
      if(files.length == 0)
        return

      let file = files[0]
      this.hasFiles = true
      this.processing = true

      this.$nextTick(() => {
        let dialogContext = this
        var importOptions = this.importOptions

        // Load the stock data
        this.requestData()

        // Read & process the DXF file
        var reader = new FileReader();
        reader.onload = (function(theFile) {
            return function(e) {

              // Read DXF & convert to an array of JSON objects for importing
              try {
                let unitsScale = 1.0
                if(importOptions.unitsType == UnitScaleTypes.inches)
                  unitsScale = 25.4

                var dxfReader = new EditorDXFReader(e.target.result, unitsScale)
                dialogContext.glassShapeDataList = dxfReader.findShapeObjectList(importOptions).map(shapeInfo => {
                  shapeInfo.import = true
                  return shapeInfo
                })

                dialogContext.processing = false

              } catch(err) {
                console.error(err)
                // Display error
                dialogContext.processing = false
                dialogContext.errorMessage('Import Error', err.message || 'Error importing')
              }
            };
          })(file);

        reader.readAsText(file);
      })
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.inventoryService.getStockList({}, { isActive: 1, type: InventoryStockTypes.glass }),
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.orderEditorService.getOrderEditorDefaultEdgeworkList()
      ])
      .then(results => {
        this.priceGroupList = results[1].data
        this.stockList = results[0].data
        this.globalDefaultEdgeworkList = results[2].data

        this.stockLookupList = this.stockList.map(stock => {
          return {
            value: stock.id,
            name: `${stock.code} | ${stock.name}`
          }
        })

        let defaultPriceGroup = this.priceGroupList.find(priceGroup => priceGroup.isDefault)
        if(defaultPriceGroup)
          this.defaultPriceGroupId = defaultPriceGroup.id
      })
      .catch(err => {
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    loadStockData() {
      if(this.orderItemData.stockId == null) {
        this.orderItemData.stock = null
        this.orderItemData.unitPrice = 0
        this.orderItemData.glassLevelId = null
        this.orderItemData.glassLevel = null
        return
      }

      this.loadingStock = true
      this.orderItemData.stock = null
      this.orderItemData.unitPrice = 0

      Promise.all([
        this.$apiService.inventoryService.getStock(this.orderItemData.stockId),
        this.$apiService.orderEditorService.getStockOrderEditorDefaultEdgeworkList(this.orderItemData.stockId)
      ])
      .then(results => {

        this.orderItemData.stock = results[0].data
        this.orderItemData.description = this.orderItemData.stock.name
        this.stockDefaultEdgeworkList = results[1].data

        // Set default price group for the stock (if it has pricing available)
        if(this.orderItemData.stockPriceGroupId == null) {
          if(this.orderItemData.stock.glassSalesPriceList) {
            if(this.orderItemData.stock.glassSalesPriceList.find(price => price.isActive && price.stockPriceGroupId == this.defaultPriceGroupId) != null) {
              this.orderItemData.stockPriceGroupId = this.defaultPriceGroupId
            }
          }
        } else {
          this.queryStockPrice()
        }
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loadingStock = false
      })
    },
    queryStockPrice() {
      if(this.orderItemData.stock == null)
        return

      // Pricing lookup
      let stockLookupFilter = {
        priceType: PriceTypes.customer,
        contactId: this.$props.contactId,
        defaultSalesRateId: this.$props.defaultSalesRateId,
        stockId: this.orderItemData.stockId,
        stockPriceGroupId: this.orderItemData.stockPriceGroupId,
        thickness: this.orderItemData.stock.thickness
      }

      this.$apiService.ordersService.getContactStockLookup(stockLookupFilter)
      .then(response => {
        this.orderItemData.unitPrice = parseFloat(response.data.price.value)
        this.orderItemData.minChargePrice = parseFloat(response.data.price.minChargePrice)
        this.orderItemData.minChargeQuantity = parseFloat(response.data.price.minChargeQuantity)
        this.orderItemData.minChargeInvoice = parseFloat(response.data.price.minChargeInvoice)

        if(response.data.price.error) {
          // Pricing error
          this.orderItemData.priceError = response.data.price.error
        }
      })
      .catch(err => {
        console.error(err)
      })
    },
    updateStampConfig() {
      if(!this.editorConfigData)
        return
      if(!this.editorConfigData.orderStampConfig)
        return

      this.orderItemData.isStampAvailable = FilterUtils.itemPassesFilter(this.editorConfigData.orderStampConfig.priceGroupFilter, parseInt(this.orderItemData.stockPriceGroupId))
      if(!this.orderItemData.isStampAvailable) {
        this.orderItemData.isStamped = false
        return
      }

      // Stamps are on by default, unless explicitly not required for a contact
      // Auto apply needed?
      if(this.editorConfigData.orderStampConfig.isAutoApply) {
        this.orderItemData.isStamped = true
        if(this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter && this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter.type == FilterOptionTypes.include) {
          this.orderItemData.isStamped = !FilterUtils.itemPassesFilter(this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter, this.contactId)
        }
      }
    },
  }
}
</script>